import { SessionTimeoutUpdate } from '@upstart/harmony/lib/legos/crossAppMessaging';
import { FinanceResponse } from '@upstart/harmony/lib/legos/financingTypes';

const hasEmbeddedInCCSearchParam =
  new URLSearchParams(window.location.search).get('embedded-in') ===
  'command-center';

const hasEmbeddedInFniHomeSearchParam =
  new URLSearchParams(window.location.search).get('embedded-in') === 'fni-home';

const hasStandaloneHistoryAndNotesParam =
  new URLSearchParams(window.location.search).get('standalone-view') ===
  'history-and-notes';

const hasEmbeddedInClosingFlowParam =
  new URLSearchParams(window.location.search).get('embedded-in') ===
  'closing-workflow';

export function isEmbeddedInCommandCenter(): boolean {
  return (
    hasEmbeddedInCCSearchParam ||
    hasEmbeddedInFniHomeSearchParam ||
    hasStandaloneHistoryAndNotesParam ||
    hasEmbeddedInClosingFlowParam
  );
}

export function isEmbeddedInFniHome(): boolean {
  return hasEmbeddedInFniHomeSearchParam || hasEmbeddedInClosingFlowParam;
}

export function isEmbeddedInClosingWorkflow(): boolean {
  return hasEmbeddedInClosingFlowParam;
}

/**
 * hides everything else except notes on Desking page (CarPencilEditor component)
 * @returns boolean
 */
export function isEmbeddedStandaloneHistoryAndNotes(): boolean {
  return hasStandaloneHistoryAndNotesParam;
}

interface DeskingDealIdChangedPayload {
  type: 'deskingDealIdChanged';
  data: {
    dealId: string;
  };
}

interface DeskingTabChangedPayload {
  type: 'deskingTabChanged';
  data: {
    dealId: string;
    tab: string;
  };
}

interface FinancingRicReviewCancel {
  type: 'financingRicReviewCancel';
  data: {
    dealId: string;
    financeResponseId: string;
  };
}

interface ClosingWorkflowUpdateRicForm {
  type: 'closingWorkflowUpdateRicForm';
  data: {
    ricFormValues: FinanceResponse;
    isRecalculating?: boolean;
  };
}

interface ClosingWorkflowReviewFinanceComplete {
  type: 'closingWorkflowReviewFinanceComplete';
  data:
    | {
        success: true;
      }
    | {
        success: false;
        // documenting known errors here for now
        error?: 'applicantDenied' | string;
      };
}

interface ClosingWorkflowSetIframeMaxHeight {
  type: 'closingWorkflowSetIframeMaxHeight';
  data: {
    maxHeight: number;
  };
}

interface ClosingWorkflowPullFromDmsResult {
  type: 'closingWorkflowPullFromDmsResult';
  data: {
    success: boolean;
    dmsDealId?: string;
    error?: string;
  };
}

interface SessionTimeoutPayload {
  type: 'chairmanSessionTimeout';
  data: {
    sessionTimeout: boolean;
  };
}

export function forwardSessionTimeoutUpdateToCC(newTimeoutInSeconds: number) {
  const payload: SessionTimeoutUpdate = {
    type: 'sessionTimeoutUpdate',
    data: {
      newTimeoutInSeconds,
    },
  };

  // calling this when not in command center can produce annoying
  // origin mismatch errors in firefox
  if (isEmbeddedInCommandCenter()) {
    window.top?.postMessage(
      payload,
      process.env.REACT_APP_COMMAND_CENTER_URL ||
        'https://autoretail.upstart.com',
    );
  }
}

type CCPayload =
  | DeskingDealIdChangedPayload
  | DeskingTabChangedPayload
  | SessionTimeoutPayload
  | FinancingRicReviewCancel
  | ClosingWorkflowUpdateRicForm
  | ClosingWorkflowReviewFinanceComplete
  | ClosingWorkflowSetIframeMaxHeight
  | ClosingWorkflowPullFromDmsResult;

function sendPayloadToCC(payload: CCPayload, forceSend = false) {
  // calling this when not in command center can produce annoying
  // origin mismatch errors in firefox
  if (isEmbeddedInCommandCenter() || forceSend) {
    window.parent.postMessage(
      payload,
      process.env.REACT_APP_COMMAND_CENTER_URL ||
        'https://autoretail.upstart.com',
    );
  }
}

export function forwardDeskingDealIdChangedToCC(dealId: string) {
  const payload: DeskingDealIdChangedPayload = {
    type: 'deskingDealIdChanged',
    data: {
      dealId,
    },
  };
  sendPayloadToCC(payload);
}

export function updateDeskingTabChangedToCC(
  data: DeskingTabChangedPayload['data'],
) {
  const payload: DeskingTabChangedPayload = {
    type: 'deskingTabChanged',
    data,
  };
  sendPayloadToCC(payload);
}

export function sendFinancingRicReviewCancelToCC(
  data: FinancingRicReviewCancel['data'],
) {
  const payload: FinancingRicReviewCancel = {
    type: 'financingRicReviewCancel',
    data,
  };
  sendPayloadToCC(payload);
}

export function signalSessionTimedOutToCC() {
  const payload: SessionTimeoutPayload = {
    type: 'chairmanSessionTimeout',
    data: {
      sessionTimeout: true,
    },
  };
  sendPayloadToCC(payload, true);
}

export function sendRicFormUpdateToCC(
  data: ClosingWorkflowUpdateRicForm['data'],
) {
  const payload: ClosingWorkflowUpdateRicForm = {
    type: 'closingWorkflowUpdateRicForm',
    data,
  };

  sendPayloadToCC(payload);
}

export function sendReviewFinanceCompleteToCC(
  data: ClosingWorkflowReviewFinanceComplete['data'],
) {
  const payload: ClosingWorkflowReviewFinanceComplete = {
    type: 'closingWorkflowReviewFinanceComplete',
    data,
  };

  sendPayloadToCC(payload);
}

export function sendIframeMaxHeightToCC(
  data: ClosingWorkflowSetIframeMaxHeight['data'],
) {
  const payload: ClosingWorkflowSetIframeMaxHeight = {
    type: 'closingWorkflowSetIframeMaxHeight',
    data,
  };

  sendPayloadToCC(payload);
}

export function sendClosingWorkflowPullFromDmsResultToCC(
  data: ClosingWorkflowPullFromDmsResult['data'],
) {
  const payload: ClosingWorkflowPullFromDmsResult = {
    type: 'closingWorkflowPullFromDmsResult',
    data,
  };

  sendPayloadToCC(payload);
}
